import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import FlightTypeService from '../services/FlightTypeService';

interface FlightTypeInfo {
  id: string;
  serviceType?: string;
  flightType?: string;
  flightTypeDescription: string;
}

interface UseFlightTypeDescriptionsReturn {
  flightTypeInfos: FlightTypeInfo[];
  flightTypeOptions: { value: string; label: string; title: string; id: string }[];
  flightTypes: string[]; // Kept for backward compatibility but computed from flightTypeInfos
  isLoading: boolean;
  error: string | null;
  getDescriptionByFlightTypeId: (id: number) => string | undefined;
  getServiceTypeByFlightTypeId: (id: number) => string | undefined;
}

export const useFlightTypeDescriptions = (): UseFlightTypeDescriptionsReturn => {
  const auth = useAuth();
  const [flightTypeInfos, setFlightTypeInfos] = useState<FlightTypeInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlightTypes = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const token = auth.user?.access_token;
        const response = await FlightTypeService.getFlightTypeDescription(token);
        
        if (response.data && Array.isArray(response.data)) {
          // Store the full flight type info objects
          const validFlightTypeInfos = response.data
            .filter(item => item && item.flightTypeDescription);

          setFlightTypeInfos(validFlightTypeInfos);
        } else {
          setError("Invalid flight type data format");
          setFlightTypeInfos([]);
        }
      } catch (err) {
        setError("Error fetching flight types");
        setFlightTypeInfos([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlightTypes();
  }, [auth.user]);

  // Create flight type options from the API data with truncated labels if needed
  const flightTypeOptions = flightTypeInfos.map(info => {
    const description = info.flightTypeDescription;
    const truncatedLabel = description.length > 50
      ? `${description.substring(0, 47)}...`
      : description;

    return {
      value: description,
      label: truncatedLabel,
      title: description, // For tooltip/title on hover
      id: info.id
    };
  });

  // Compute flightTypes from flightTypeInfos for backward compatibility
  const flightTypes = flightTypeInfos.map(info => info.flightTypeDescription);

  const getDescriptionByFlightTypeId = (id: number): string | undefined => {
    // Defensive check in case id is undefined
    if (!id) {
      console.warn('Cannot get flight type description: Id is undefined or empty');
      return undefined;
    }

    // Find the flight type with the matching Id
    // Convert both to numbers for comparison since API returns number ids
    const flightTypeInfo = flightTypeInfos.find(f => Number(f.id) === Number(id));
    return flightTypeInfo?.flightTypeDescription;
  };

  const getServiceTypeByFlightTypeId = (id: number): string | undefined => {
    // Defensive check in case id is undefined
    if (!id) {
      return undefined;
    }

    // Find the flight type with the matching Id
    const flightTypeInfo = flightTypeInfos.find(f => Number(f.id) === Number(id));
    const serviceTypeCode = flightTypeInfo?.serviceType;

    // Map service type codes to descriptive names
    if (!serviceTypeCode) {
      return undefined;
    }

    const serviceTypeMap: { [key: string]: string } = {
      'S': 'Scheduled',
      'C': 'Charter',
      'N': 'Non-Revenue',
      'O': 'Other Non-OP',
      'U': 'Unassigned',
      'L': 'Lease',
      'A': 'Amazon'
    };

    return serviceTypeMap[serviceTypeCode] || serviceTypeCode;
  };

  return {
    flightTypes,
    flightTypeInfos,
    flightTypeOptions,
    isLoading,
    error,
    getDescriptionByFlightTypeId,
    getServiceTypeByFlightTypeId
  };
};
